import React from "react";
import {
  FaBell,
  FaEdit,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  canSendReminder,
  formatDateFR,
  formatEuro,
  getArticleName,
  getPaymentKindLabel,
  getReminderCount,
  getStatusBadgeClass,
  getUserFullName,
} from "../utils/paymentsDashboardUtils";

function DetailLine({ label, value, strong }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p
        className={`mt-1 text-sm ${
          strong
            ? "font-bold text-slate-900 dark:text-white"
            : "font-medium text-slate-700 dark:text-slate-200"
        }`}
      >
        {value || "—"}
      </p>
    </div>
  );
}

export default function PaymentDetailsDrawer({
  isOpen,
  payment,
  deletingId,
  remindingId,
  togglingAuthorizationId,
  onClose,
  onReminder,
  onOpenReminderHistory,
  onEdit,
  onDelete,
  onToggleAuthorization,
}) {
  if (!isOpen || !payment) return null;

  const isAuthorized = !!payment.autorisation_achat;
  const reminderAvailable = canSendReminder(payment);

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Détail paiement
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {getArticleName(payment)}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {getUserFullName(payment.User)}
              {payment.User?.email ? ` · ${payment.User.email}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailLine
              label="Montant"
              value={formatEuro(payment.prix_total)}
              strong
            />

            <DetailLine label="Date" value={formatDateFR(payment.date_achat)} />

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Statut
              </p>

              <span
                className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${getStatusBadgeClass(
                  payment.statut_reglement,
                )}`}
              >
                {payment.statut_reglement || "—"}
              </span>
            </div>

            <DetailLine
              label="Moyen de règlement"
              value={payment.type_reglement || "—"}
            />

            <DetailLine
              label="Type d’achat"
              value={getPaymentKindLabel(payment)}
            />

            <DetailLine label="Quantité" value={payment.quantite ?? "—"} />

            <DetailLine label="Taille" value={payment.Article?.taille || "—"} />

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Autorisation côté utilisateur
              </p>

              <button
                type="button"
                onClick={() => onToggleAuthorization(payment)}
                disabled={togglingAuthorizationId === payment.id}
                className={`mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isAuthorized
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                    : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                }`}
              >
                {isAuthorized ? <FaEye /> : <FaEyeSlash />}
                {isAuthorized ? "Visible" : "Masqué"}
              </button>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/20 dark:bg-amber-500/10">
              <p className="text-[11px] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                Rappels envoyés
              </p>

              <button
                type="button"
                onClick={() => onOpenReminderHistory(payment)}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200 transition hover:bg-amber-100 dark:bg-slate-900 dark:text-amber-300 dark:ring-amber-500/20 dark:hover:bg-slate-800"
              >
                <FaBell />
                {getReminderCount(payment)} rappel(s)
              </button>
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              Message utilisateur
            </p>

            <div className="mt-2 whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:bg-slate-900 dark:text-slate-200">
              {payment.message_achat || "Aucun message renseigné."}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            {reminderAvailable ? (
              <button
                type="button"
                onClick={() => onReminder(payment)}
                disabled={remindingId === payment.id}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              >
                <FaEnvelope />
                {remindingId === payment.id ? "Envoi..." : "Rappel"}
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => onEdit(payment)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <FaEdit />
              Modifier
            </button>

            <button
              type="button"
              onClick={() => onDelete(payment)}
              disabled={deletingId === payment.id}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <button
              type="button"
              onClick={() => onOpenReminderHistory(payment)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm font-bold text-amber-700 transition hover:bg-amber-50 dark:border-amber-500/20 dark:bg-slate-900 dark:text-amber-300 dark:hover:bg-slate-800"
            >
              <FaBell />
              Historique ({getReminderCount(payment)})
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
